import React from 'react';
import Modal from '../../common/Modal';
import FormField from '../../common/FormField';
import { useFormData } from '../../../hooks/useFormData';

/**
 * Item add/edit modal.
 *
 * Before: 415 lines — duplicate style objects, hand-coded labels/inputs.
 * After:  ~160 lines — uses shared Modal, FormField, and useFormData.
 *         Unique item logic (numeric cleanup, FK validation) stays here.
 */

const DEFAULTS = {
  item_id: '',
  sales_description: '',
  purchase_description: '',
  stock_no: '',
  quantity_on_hand: 0,
  initial_quantity: 0,
  selling_price_excl: 0,
  selling_price_incl: 0,
  cost_excl: 0,
  category_id: '',
  supplier_id: '',
  sales_tax_code: 'GST',
  purchase_tax_code: 'GST',
  selling_unit: 1,
  purchase_unit: 1,
  selling_unit_name: 'None',
  purchase_unit_name: 'None',
  is_service_item: false,
  is_inactive: false,
};

export default function ItemModal({
  isOpen, onClose, onSubmit, initialData, title,
  categories = [], suppliers = [], taxes = [],
}) {
  const { formData, handleChange } = useFormData(DEFAULTS, initialData, isOpen);

  const handleSubmit = () => {
    // Whitelist only the columns that exist on the writable `item` table.
    // Reading happens against the `item_with_stock` view, which carries
    // computed/joined fields (e.g. calculated_stock, category, supplier)
    // that the base table rejects on write.
    const final = Object.keys(DEFAULTS).reduce((acc, key) => {
      acc[key] = formData[key];
      return acc;
    }, {});

    if (!final.purchase_description && final.sales_description) {
      final.purchase_description = final.sales_description;
    }

    // Convert empty FK fields to null
    if (final.category_id === '') final.category_id = null;
    if (final.supplier_id === '') final.supplier_id = null;

    // Ensure numeric fields aren't NaN
    const numericFields = [
      'quantity_on_hand', 'initial_quantity', 'selling_price_excl',
      'selling_price_incl', 'cost_excl', 'selling_unit', 'purchase_unit',
    ];
    numericFields.forEach((f) => {
      if (Number.isNaN(final[f]) || final[f] === '') final[f] = 0;
    });

    onSubmit(final);
  };

  // Shared select styling
  const selectClass =
    'w-full px-2 py-2 text-sm border border-gray-300 rounded font-[Arial] bg-white focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} onSubmit={handleSubmit} title={title} width="max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        {/* Item ID — full width, disabled when editing */}
        <div className="col-span-2">
          <FormField name="item_id" label="Item ID / Code (Primary Key)" value={formData.item_id} onChange={handleChange} required disabled={!!initialData} />
        </div>

        {/* Checkboxes (edit mode only) */}
        {initialData && (
          <div className="col-span-2 flex gap-5 mb-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" name="is_service_item" checked={formData.is_service_item || false} onChange={handleChange} />
              Service Item
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" name="is_inactive" checked={formData.is_inactive || false} onChange={handleChange} />
              Inactive
            </label>
          </div>
        )}

        <div className="col-span-2">
          <FormField name="sales_description" label="Sales Desc. (Required)" value={formData.sales_description} onChange={handleChange} required />
        </div>
        <div className="col-span-2">
          <FormField name="purchase_description" label="Purchase desc." value={formData.purchase_description} onChange={handleChange} />
        </div>

        <FormField name="initial_quantity" label="Init. Quantity" value={formData.initial_quantity} onChange={handleChange} type="number" />
        <FormField name="quantity_on_hand" label="Qty" value={formData.quantity_on_hand} onChange={handleChange} type="number" />
        <FormField name="stock_no" label="Stock No" value={formData.stock_no} onChange={handleChange} />
        <FormField name="cost_excl" label="Cost w/o Tax" value={formData.cost_excl} onChange={handleChange} type="number" step="0.01" />
        <FormField name="selling_price_incl" label="Price w/ Tax" value={formData.selling_price_incl} onChange={handleChange} type="number" step="0.01" />
        <FormField name="selling_price_excl" label="Price w/o Tax" value={formData.selling_price_excl} onChange={handleChange} type="number" step="0.01" />
        <FormField name="selling_unit" label="Selling Unit" value={formData.selling_unit} onChange={handleChange} type="number" />
        <FormField name="selling_unit_name" label="Selling Unit Name" value={formData.selling_unit_name} onChange={handleChange} placeholder="e.g. ea, box" />
        <FormField name="purchase_unit" label="Purchase Unit" value={formData.purchase_unit} onChange={handleChange} type="number" />
        <FormField name="purchase_unit_name" label="Purchase Unit Name" value={formData.purchase_unit_name} onChange={handleChange} placeholder="e.g. carton" />

        {/* Dropdowns for relationships */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select name="category_id" value={formData.category_id || ''} onChange={handleChange} className={selectClass}>
            <option value="">Select Category</option>
            {categories.map((c) => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
          <select name="supplier_id" value={formData.supplier_id || ''} onChange={handleChange} className={selectClass}>
            <option value="">Select Supplier</option>
            {suppliers.map((s) => (
              <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sales Tax Code</label>
          <select name="sales_tax_code" value={formData.sales_tax_code || ''} onChange={handleChange} className={selectClass}>
            {taxes.map((t) => (
              <option key={t.tax_id} value={t.tax_id}>{t.tax_id} ({t.tax_rate}%)</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Tax Code</label>
          <select name="purchase_tax_code" value={formData.purchase_tax_code || ''} onChange={handleChange} className={selectClass}>
            {taxes.map((t) => (
              <option key={t.tax_id} value={t.tax_id}>{t.tax_id} ({t.tax_rate}%)</option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
